import React, { ChangeEvent, useState } from 'react';
import { Avatar, Badge, Box, Button, Hidden, IconButton, Stack, Tooltip } from '@mui/material';
import { Employee } from '../../types/Employee';
import { PhotoCameraRounded } from '@mui/icons-material';
import { getStorage, ref, uploadString } from 'firebase/storage';
import { doc, getFirestore, setDoc } from 'firebase/firestore';
import { DateTime } from 'luxon';

type Props = {
  employee: Employee;
  onUpload?: () => void;
};
export const EmployeeUpdateProfilePhoto = (props: Props) => {
  const { employee, onUpload } = props;

  const size = 96;
  const height = 100;
  const width = 400;

  const [data, setData] = useState('');

  const handleInputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(event.target.files[0]);
      fileReader.onload = (e) => {
        if (e.target && e.target.result && typeof e.target.result === 'string') {
          setData(e.target.result);
        }
      };
    }
  };

  const handleClearOnClick = () => {
    setData('');
  };

  const handleUploadOnClick = () => {
    if (data) {
      const storage = getStorage();
      const db = getFirestore();

      const name = 'photo_' + DateTime.utc().toFormat('yyyyMMdd_hhmmss') + '_' + DateTime.utc().toMillis();
      const dest = `${employee.idNumber}/Photos/${name}`;
      uploadString(ref(storage, dest), data, 'data_url')
        .then(() => {
          const url = "https://firebasestorage.googleapis.com/v0/b/nadafa-ems.appspot.com/o/" + encodeURIComponent(dest) + "?alt=media";
          return setDoc(doc(db, `Employees/${employee.idNumber}`), { picture: url }, { merge: true });
        })
        .then(() => {
          onUpload && onUpload();
          setData('');
        });
    }
  }

  return (
    <Box>
      <Box sx={{ 
        position: 'relative',
        height: height + (size / 2)
      }}>
        <Box sx={{
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: 'pink',
          height: height,
          width: width,
        }}>
          <svg id="visual" viewBox="0 0 400 100" width="400" height="100" xmlns="http://www.w3.org/2000/svg" version="1.1"><rect x="0" y="0" width="400" height="100" fill="#001220"></rect><defs><linearGradient id="grad1_0" x1="75%" y1="0%" x2="100%" y2="100%"><stop offset="-30%" stop-color="#fbae3c" stop-opacity="1"></stop><stop offset="130%" stop-color="#fbae3c" stop-opacity="1"></stop></linearGradient></defs><defs><linearGradient id="grad1_1" x1="75%" y1="0%" x2="100%" y2="100%"><stop offset="-30%" stop-color="#fbae3c" stop-opacity="1"></stop><stop offset="130%" stop-color="#96446e" stop-opacity="1"></stop></linearGradient></defs><defs><linearGradient id="grad1_2" x1="75%" y1="0%" x2="100%" y2="100%"><stop offset="-30%" stop-color="#001220" stop-opacity="1"></stop><stop offset="130%" stop-color="#96446e" stop-opacity="1"></stop></linearGradient></defs><defs><linearGradient id="grad2_0" x1="0%" y1="0%" x2="25%" y2="100%"><stop offset="-30%" stop-color="#fbae3c" stop-opacity="1"></stop><stop offset="130%" stop-color="#fbae3c" stop-opacity="1"></stop></linearGradient></defs><defs><linearGradient id="grad2_1" x1="0%" y1="0%" x2="25%" y2="100%"><stop offset="-30%" stop-color="#96446e" stop-opacity="1"></stop><stop offset="130%" stop-color="#fbae3c" stop-opacity="1"></stop></linearGradient></defs><defs><linearGradient id="grad2_2" x1="0%" y1="0%" x2="25%" y2="100%"><stop offset="-30%" stop-color="#96446e" stop-opacity="1"></stop><stop offset="130%" stop-color="#001220" stop-opacity="1"></stop></linearGradient></defs><g transform="translate(400, 0)"><path d="M0 100C-10.6 97.7 -21.2 95.4 -29.4 90.4C-37.5 85.3 -43.3 77.6 -52.9 72.8C-62.5 68.1 -76 66.3 -80.9 58.8C-85.8 51.3 -82 38 -83.7 27.2C-85.4 16.4 -92.7 8.2 -100 0L0 0Z" fill="#3d3056"></path><path d="M0 66.7C-7.1 65.1 -14.1 63.6 -19.6 60.2C-25 56.9 -28.8 51.7 -35.3 48.5C-41.7 45.4 -50.7 44.2 -53.9 39.2C-57.2 34.2 -54.6 25.3 -55.8 18.1C-57 11 -61.8 5.5 -66.7 0L0 0Z" fill="#e1685e"></path><path d="M0 33.3C-3.5 32.6 -7.1 31.8 -9.8 30.1C-12.5 28.4 -14.4 25.9 -17.6 24.3C-20.8 22.7 -25.3 22.1 -27 19.6C-28.6 17.1 -27.3 12.7 -27.9 9.1C-28.5 5.5 -30.9 2.7 -33.3 0L0 0Z" fill="#fbae3c"></path></g><g transform="translate(0, 100)"><path d="M0 -100C10.9 -99.8 21.8 -99.5 30.9 -95.1C40 -90.7 47.4 -82 54.1 -74.4C60.7 -66.9 66.6 -60.4 73.6 -53.5C80.6 -46.6 88.6 -39.3 93.2 -30.3C97.8 -21.3 98.9 -10.6 100 0L0 0Z" fill="#3d3056"></path><path d="M0 -66.7C7.3 -66.5 14.5 -66.4 20.6 -63.4C26.7 -60.4 31.6 -54.7 36.1 -49.6C40.5 -44.6 44.4 -40.3 49.1 -35.7C53.7 -31.1 59.1 -26.2 62.1 -20.2C65.2 -14.2 65.9 -7.1 66.7 0L0 0Z" fill="#e1685e"></path><path d="M0 -33.3C3.6 -33.3 7.3 -33.2 10.3 -31.7C13.3 -30.2 15.8 -27.3 18 -24.8C20.2 -22.3 22.2 -20.1 24.5 -17.8C26.9 -15.5 29.5 -13.1 31.1 -10.1C32.6 -7.1 33 -3.5 33.3 0L0 0Z" fill="#fbae3c"></path></g></svg>
        </Box>
        <Stack direction='row' sx={{
          position: 'absolute',
          top: height - (size / 2),
          left: (width / 2) - (size / 2),
        }}>
          <Tooltip title="Click to browser images...">
            <>
              <input
                accept="image/*"
                id="icon-button-image"
                type="file"
                name="file"
                onChange={handleInputOnChange}
                hidden
              />
              <label htmlFor="icon-button-image">
                <IconButton component="span" sx={{ p: 0 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <PhotoCameraRounded fontSize='large' />
                    }
                  >
                    <Avatar src={data !== '' ? data : employee.picture} sx={{ width: size, height: size }} />
                  </Badge>
                </IconButton>
              </label>
            </>
          </Tooltip>
        </Stack>
      </Box>
      <Stack marginTop={4} direction='row' spacing={1} justifyContent='center'>
        <Button sx={{ flex: 1 }} onClick={handleUploadOnClick}>Upload</Button>
        <Button sx={{ flex: 1 }} color='error' onClick={handleClearOnClick}>Clear</Button>
      </Stack>
    </Box>
  );
};
